import { FC, useMemo } from 'react';
import {
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from '@mui/material';
import TableSelectField, { TableSelectColumn } from '../TableSelectFieldv2';
import DataSourceIcon from '../DataSourceIcon';
import { GAME_STATUS_MAP, GameStatus } from '@constants/constants';

type DashboardHeaderOption = {
  id?: number | string;
  label?: string; // inorder to be backward compatible
  [key: string]: unknown;
};

interface DashboardHeaderFieldProps {
  title: string;
  options: DashboardHeaderOption[];
  selectedId: number | string | null;
  onChange: (id: number | string | null) => void;
  disabled?: boolean;
  displayKey?: keyof DashboardHeaderOption;
  searchKeys?: string[];
  searchPlaceholder?: string;
  handleRenderValue?: (option: DashboardHeaderOption) => string;
  dynamicColumnsTypes?: TableSelectColumn<DashboardHeaderOption>[];
  selectBoxStyles?: SxProps<Theme>;
  titleStyles?: SxProps<Theme>;
}

// This is for backward compatibility
const DEFAULT_COLUMNS_TYPES: TableSelectColumn<DashboardHeaderOption>[] = [
  { key: 'id', label: 'Game ID', width: '60px' },
  {
    key: 'dataSource',
    label: 'Data Source',
    width: '28px',
    render: (value) => <DataSourceIcon dataSource={value as string} size={24} />,
  },
  { key: 'label', label: 'Deal Name', width: '215px' },
  {
    key: 'gameFundedStatus',
    label: 'Funding Status',
    render: (value) => GAME_STATUS_MAP[value as GameStatus] ?? '',
  },
];

const DashboardHeader: FC<DashboardHeaderFieldProps> = ({
  title,
  options,
  selectedId,
  onChange,
  disabled,
  displayKey = 'label',
  searchKeys = ['id', 'label'],
  searchPlaceholder = 'Search by Game ID or Deal Name',
  handleRenderValue = (option) => `${String(option.label ?? '')} - ${String(option.id ?? '')}`,
  dynamicColumnsTypes = [],
  selectBoxStyles = {},
  titleStyles = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) =>
        String(a?.[displayKey] ?? '').localeCompare(String(b?.[displayKey] ?? '')),
      ),
    [options, displayKey],
  );

  // Match with allowed columns here
  const dynamicColumns = useMemo(() => {
    const optionKeys = new Set<string>();
    for (const option of options) {
      Object.keys(option ?? {}).forEach((key) => optionKeys.add(key));
    }
    return (dynamicColumnsTypes?.length ? dynamicColumnsTypes : DEFAULT_COLUMNS_TYPES).filter(
      (column) => optionKeys.has(String(column.key)),
    );
  }, [options, dynamicColumnsTypes]);

  return (
    <Box>
      <Stack direction={isMobile ? 'column' : 'row'} gap={'0.5rem'} alignItems={'center'}>
        {title && (
          <Typography
            sx={{
              fontSize: isMobile ? '1.5rem' : '2rem',
              lineHeight: '30px',
              fontWeight: 700,
              color: '#162936',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
              ...titleStyles,
            }}
          >
            {title} {options.length > 0 && ':'}
            {options.length === 1 ? (options[0] as any)[displayKey] : ''}
          </Typography>
        )}

        {options.length > 1 ? (
          <TableSelectField<(typeof sortedOptions)[number]>
            label={''}
            options={sortedOptions}
            columns={dynamicColumns}
            valueKey="id"
            displayKey={displayKey as string}
            searchKeys={searchKeys}
            selectedValue={selectedId ?? undefined}
            onChange={(value) => onChange((value as number | string) ?? null)}
            disabled={disabled}
            centerDropdown
            renderValue={handleRenderValue}
            renderValueAdornment={(option) => {
              const ds = option.dataSource as string | undefined;
              if (!ds) return null;
              return (
                <InputAdornment position="start" sx={{ marginRight: '6px', marginLeft: '2px' }}>
                  <DataSourceIcon dataSource={ds} size={24} />
                </InputAdornment>
              );
            }}
            searchPlaceholder={searchPlaceholder}
            selectStyles={{
              width: isMobile ? '200px' : '350px',
              '& .MuiOutlinedInput-root': {
                fontSize: '22px',
                color: '#162936',
                lineHeight: '30px',
                fontWeight: 700,
                borderRadius: '8px',
                border: '1px solid #2F736E46',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              },
              '& .MuiSvgIcon-root': { fill: '#2F736E !important' },
              ...selectBoxStyles,
            }}
          />
        ) : (
          <></>
        )}
      </Stack>
    </Box>
  );
};
export default DashboardHeader;
